import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useEffect, useState } from "react";
import { Download, ShieldCheck } from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { absoluteUrl, breadcrumbJsonLd, ldScript, pageHead } from "@/lib/seo";

const PATH = "/read-barcode-app";
const TITLE = "Read Barcode App — Free Installable Barcode Scanner";
const DESC =
  "ReadBarcode is a free read barcode app you install on phone, tablet or desktop. Scan UPC, EAN, QR, Data Matrix and more — offline, no signup, no app store.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Readers", path: "/readers" },
  { name: "Read Barcode App", path: PATH },
];

const FAQ = [
  {
    q: "Is the read barcode app free?",
    a: "Yes. ReadBarcode is completely free — no subscription, no signup, no in-app purchases. You install the same web app every tool on this site already runs in; installing just adds a home-screen icon and offline support.",
  },
  {
    q: "Do I Need to Download It From the App Store or Google Play?",
    a: "No. ReadBarcode is a Progressive Web App (PWA), so it installs straight from the browser — there is no app-store listing, no review wait and no separate download. On iOS use Safari's Share menu; on Android Chrome offers an Install prompt; on desktop the address bar shows an install icon.",
  },
  {
    q: "Does the read barcode app work offline?",
    a: "The browser-based tools do. Once you have opened the app, the barcode reader, QR and barcode generators, and the GTIN / check-digit validators keep working with no connection — decoding and generation run entirely on your device. Lookups against external product databases still need the network.",
  },
  {
    q: "Which Barcodes Can the App Scan and Read?",
    a: "Every common 1D and 2D symbology: UPC-A, UPC-E, EAN-13, EAN-8, Code 128, Code 39, ITF-14, QR Code, Data Matrix, PDF417 and Aztec. Point the camera at a barcode or upload a photo and it decodes automatically.",
  },
  {
    q: "Is My Camera or Scanned Data Sent Anywhere?",
    a: "No. The camera stream and every scanned value stay on your device — decoding happens in the browser. There is no account, no server-side storage and no telemetry on what you scan.",
  },
  {
    q: "What devices does the read barcode app run on?",
    a: "Any device with a modern browser — iPhone and iPad (Safari), Android phones and tablets (Chrome, Edge, Firefox), and Windows / macOS / Linux desktops (Chrome, Edge). The same app installs on all of them.",
  },
  {
    q: "Is There a Free Barcode Scanner App?",
    a: "Yes. ReadBarcode is free to use and to install, with no account and no in-app purchases. It reads UPC, EAN, Code 128, QR, Data Matrix and other common formats from the camera or from an uploaded image.",
  },
  {
    q: "How Do Barcode Scanner Apps Work?",
    a: "The app takes frames from the camera and searches each one for the pattern of a barcode. When it finds one, it measures the bars or modules, turns them into characters and checks the result against the format's check digit or error correction. ReadBarcode does this in the browser with the open-source ZXing library, frame after frame, until a code is read.",
  },
  {
    q: "Is a QR Code Scanner App Safe?",
    a: "Scanning is safe in itself; the risk is the link inside the code, which can lead to a phishing page just like a link in an email. Read the decoded address before you open it, especially on stickers placed over posters or parking signs. The QR Code Reader on this site shows a scanned link for review and never opens it automatically.",
  },
];

export const Route = createFileRoute("/read-barcode-app")({
  component: ReadBarcodeAppPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
      ogType: "website",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "ReadBarcode — Read Barcode App",
          description: DESC,
          url: absoluteUrl(PATH),
          applicationCategory: "UtilitiesApplication",
          operatingSystem: "Any (Web / iOS / Android / Windows / macOS)",
          browserRequirements: "Requires a modern browser with JavaScript.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

/**
 * The shape of the `beforeinstallprompt` event isn't in lib.dom yet.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
  prompt(): Promise<void>;
}

/**
 * Live install affordance for the read barcode app. Captures the
 * `beforeinstallprompt` event when the browser fires it (Chrome / Edge /
 * Android) and offers a one-tap install; otherwise it points the reader at
 * the per-platform "Add to Home Screen" steps below — the universal
 * fallback for iOS Safari, which never fires the event.
 */
function InstallAppButton() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Already running as an installed app.
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
      return;
    }
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPromptEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPromptEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) {
    return (
      <p className="inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-4 py-2 text-sm text-foreground">
        <ShieldCheck className="h-4 w-4 text-primary" aria-hidden />
        The app is installed — open it from your home screen.
      </p>
    );
  }

  if (promptEvent) {
    return (
      <button
        type="button"
        onClick={async () => {
          await promptEvent.prompt();
          try {
            await promptEvent.userChoice;
          } catch {
            /* either outcome is fine */
          }
          setPromptEvent(null);
        }}
        className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
      >
        <Download className="h-4 w-4" aria-hidden />
        Install the read barcode app
      </button>
    );
  }

  return (
    <a
      href="#install"
      className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-5 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
    >
      <Download className="h-4 w-4" aria-hidden />
      How to install the app
    </a>
  );
}

type Benefit = { title: string; body: string };
const BENEFITS: Benefit[] = [
  {
    title: "Works Offline",
    body: "Once opened, the reader, generators and validators keep working with no connection — every decode runs on your device.",
  },
  {
    title: "Installs on Any Device",
    body: "iPhone, iPad, Android, Windows, macOS and Linux. The same app, one home-screen icon, its own window.",
  },
  {
    title: "Private by Design",
    body: "No account, no upload. The camera stream and every scanned value stay on the device — nothing is logged.",
  },
  {
    title: "No App Store",
    body: "Installs straight from the browser as a Progressive Web App — no store listing, no review wait, no bulky download.",
  },
];

type AppTool = { path: string; label: string; desc: string };
const APP_TOOLS: AppTool[] = [
  {
    path: "/barcode-reader",
    label: "Online Barcode Reader",
    desc: "Scan any 1D or 2D barcode from camera or image.",
  },
  {
    path: "/qr-code-reader",
    label: "QR Code Reader",
    desc: "Decode QR codes — URLs, Wi-Fi, vCard and text payloads.",
  },
  {
    path: "/read-barcode-from-image",
    label: "Read Barcode From Image",
    desc: "Upload a photo or screenshot to extract the value.",
  },
  {
    path: "/data-matrix-reader",
    label: "Data Matrix Reader",
    desc: "Decode plain and GS1 Data Matrix codes.",
  },
  {
    path: "/barcode-generator",
    label: "Barcode Generator",
    desc: "Create UPC, EAN, Code 128, ITF-14 and more.",
  },
  {
    path: "/qr-code-generator",
    label: "QR Code Generator",
    desc: "Generate QR codes with custom error correction.",
  },
  {
    path: "/gtin-validator",
    label: "GTIN Validator",
    desc: "Confirm a UPC / EAN / GTIN check digit.",
  },
  {
    path: "/barcode-type-detector",
    label: "Barcode Type Detector",
    desc: "Identify the format behind an unknown value.",
  },
];

function ReadBarcodeAppPage() {
  return (
    <ToolLayout
      eyebrow="Installable app"
      title="Read Barcode App — Scan & Decode on Any Device"
      description="A free, installable read barcode app that runs entirely in your browser. Scan UPC, EAN, QR, Data Matrix and more — offline-capable, private, with nothing to download from an app store. Once installed, it opens in its own window and loads pages you have already visited without a connection."
      crumbs={CRUMBS}
      content={
        <>
          <p>
            <strong>ReadBarcode is a read barcode app</strong> you install straight from the browser.
            It is the same toolkit every page on this site already runs — a barcode and QR reader, a
            set of generators, and the GTIN and check-digit validators — packaged as a Progressive Web
            App so it earns a home-screen icon, opens in its own window and keeps working without a
            connection.
          </p>
          <p>
            There is nothing to download from the App Store or Google Play, no account to create and
            no subscription. Point your camera at a barcode and the read barcode app decodes it on the
            spot; the camera feed and the scanned value never leave your device.
          </p>

          <h2>Why install the read barcode app</h2>
          <p>Installing adds a home-screen icon, a separate window and offline access to pages you have opened, while the scanning itself stays the same.</p>
          {BENEFITS.map((b) => (
            <Fragment key={b.title}>
              <h3>{b.title}</h3>
              <p>{b.body}</p>
            </Fragment>
          ))}

          <h2 id="install">How to Install the App</h2>
          <p>The read barcode app installs from the browser in a few taps — pick your platform:</p>
          <h3>iPhone &amp; iPad — Safari</h3>
          <ol>
            <li>Open this site in Safari.</li>
            <li>Tap the Share button.</li>
            <li>Choose &quot;Add to Home Screen&quot;.</li>
            <li>Tap Add — the icon appears on your home screen.</li>
          </ol>
          <h3>Android — Chrome</h3>
          <ol>
            <li>Open this site in Chrome.</li>
            <li>Tap the Install button above, or the ⋮ menu.</li>
            <li>Choose &quot;Install app&quot; / &quot;Add to Home screen&quot;.</li>
            <li>Confirm — it installs like any app.</li>
          </ol>
          <h3>Desktop — Chrome / Edge</h3>
          <ol>
            <li>Open this site in Chrome or Edge.</li>
            <li>Click the install icon in the address bar.</li>
            <li>Or use the Install button above.</li>
            <li>The app opens in its own window.</li>
          </ol>

          <h2>What&apos;s in the read barcode app</h2>
          <p>Every ReadBarcode tool is part of the installed app. The most-used ones:</p>
          <ul>
            {APP_TOOLS.map((t) => (
              <li key={t.path}>
                <Link to={t.path}>{t.label}</Link>
                {` ${t.desc}`}
              </li>
            ))}
          </ul>
          <p>
            See the full set on the <Link to="/readers">readers</Link> and <Link to="/generators">generators</Link> hubs.
          </p>

          <h2>Barcode Scanner App vs Installing a Web App</h2>
          <p>A store barcode scanner app and the installed ReadBarcode web app both read codes through the phone camera; they differ in how they reach your device. A store app comes through a barcode scanner app download from the App Store or Google Play, while the web app is added from the browser.</p>
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th scope="col">Question</th>
                  <th scope="col">Store barcode scanner app</th>
                  <th scope="col">ReadBarcode web app</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>How you get it</td>
                  <td>Download from the App Store or Google Play</td>
                  <td>Add to the home screen from Safari, Chrome or Edge</td>
                </tr>
                <tr>
                  <td>Where scans are decoded</td>
                  <td>Depends on the app</td>
                  <td>In the browser, on your device</td>
                </tr>
                <tr>
                  <td>Updates</td>
                  <td>Installed through the store</td>
                  <td>Loaded the next time you open it online</td>
                </tr>
                <tr>
                  <td>Computers</td>
                  <td>Only if the developer ships a desktop version</td>
                  <td>Installs on Windows, macOS and Linux</td>
                </tr>
              </tbody>
            </table>
          </TableScroll>
          <p>The web app suits anyone looking for a barcode scanner app for PC. Chrome and Edge install it on a computer, and the <Link to="/barcode-reader">online barcode reader</Link> decodes codes through a webcam or from an uploaded image.</p>
          <h2>Do You Need an App to Scan Barcodes?</h2>
          <p>No. A web browser with camera access is enough, because the page can open the camera and decode each frame on the device. Installing adds convenience, not scanning ability.</p>
          <p>A browser scan needs three things:</p>
          <ul>
            <li>A current browser with camera support, such as Safari, Chrome, Edge or Firefox.</li>
            <li>Camera permission for the site, granted once.</li>
            <li>Good light, or a saved photo of the code to upload instead.</li>
          </ul>
          <p>Some tasks have their own page. To keep count of stock, the <Link to="/inventory-scanner">inventory scanner</Link> stores each code with a quantity and location, and the <Link to="/batch-barcode-scanner">batch barcode scanner</Link> checks a pasted list of codes in one pass. As a barcode scanner app for books, scan the EAN-13 on the back cover, which starts with 978 or 979, then check it in the ISBN barcode reader.</p>
          <h3>Barcode Scanner App for iPhone</h3>
          <p>On iPhone, open the barcode reader in Safari, allow camera access when asked and hold the rear camera over the code. For one-tap access later, use Share, then Add to Home Screen.</p>
          <p>If you denied camera access by mistake, tap the page settings button in the Safari address bar and open Website Settings to allow the camera again. The same reader also works as a QR and barcode scanner app, since it reads QR codes and 1D barcodes in one view.</p>
          <h3>Barcode Scanner App for Android</h3>
          <p>On Android, Chrome shows an Install button on this page when the app can be installed, and the menu offers Install app otherwise. The installed app opens in its own window and appears among your other apps.</p>
          <p>A long press on the icon shows shortcuts to the barcode reader, the QR code generator and the GTIN validator.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <section aria-label="Install" className="rounded-lg border border-border bg-muted/30 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-xl">
            <h2 className="text-lg font-semibold tracking-tight">Install ReadBarcode as an App</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Add the read barcode app to your home screen — it opens in its own window, works
              offline and launches as fast as any installed app.
            </p>
          </div>
          <div className="shrink-0">
            <InstallAppButton />
          </div>
        </div>
      </section>
    </ToolLayout>
  );
}

const RELATED: { to: string; label: string; desc: string }[] = [
  {
    to: "/barcode-reader",
    label: "Online Barcode Reader",
    desc: "Scan a barcode with the camera, upload an image or paste an image URL.",
  },
  {
    to: "/inventory-scanner",
    label: "Inventory Scanner",
    desc: "Scan items into a local stock list with quantity and location, then export it to CSV.",
  },
  {
    to: "/batch-barcode-scanner",
    label: "Batch Barcode Scanner",
    desc: "Clean, deduplicate and check a pasted list of barcodes in one pass.",
  },
  {
    to: "/readers",
    label: "Barcode & QR Code Readers",
    desc: "Every reader on the site, from QR and Data Matrix to ISBN.",
  },
];

function RelatedTools() {
  return (
    <section className="mt-12">
      <h2 className="text-xl font-semibold tracking-tight md:text-2xl">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {RELATED.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className="group rounded-lg border border-border bg-background p-4 transition hover:border-primary/50 hover:bg-muted/40"
          >
            <div className="text-sm font-semibold">{t.label}</div>
            <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
